import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MessageSquare } from 'lucide-react';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import Button from '../../../shared/components/ui/Button';
import { addLegalCaseNote } from '../../services/api/implementation/impl';
import {
    NOTE_MAX_LENGTH,
    canWriteLegalRecords,
    isLogNote,
    logEntryLabel,
    logThreads
} from './legalCase';

const formatDateTime = (value) => {
    if (!value) return '---';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const byline = (row) => `${formatDateTime(row.createdAt)}${row.user?.name ? ` · ${row.user.name}` : ''}`;

/**
 * A court case's log: every change the API records on the case — case edits,
 * hearings, follow-ups, documents, target dates — with comments on any entry.
 * Entries are written by the API as things happen; nothing is typed in here
 * except comments. A court case has no subtasks, so there are no subtask logs
 * to show instead.
 */
const LegalCaseLogPanel = ({ task }) => {
    const queryClient = useQueryClient();
    // The entry whose comment box is open, and what is typed in it.
    const [replyTo, setReplyTo] = useState(null);
    const [reply, setReply] = useState('');
    const canWrite = canWriteLegalRecords(task);
    const entries = logThreads(task?.history);

    const commentMutation = useMutation({
        mutationFn: ({ text, parentId }) => addLegalCaseNote(task.id, text, parentId),
        onSuccess: () => {
            toast.success('Comment added');
            setReplyTo(null);
            setReply('');
            queryClient.invalidateQueries(['managementTask']);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to add the comment');
        }
    });

    const openReply = (entryId) => {
        setReplyTo(replyTo === entryId ? null : entryId);
        setReply('');
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-800">Logs</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Newest first — every change to the case is recorded here automatically. Comment on any entry.
                </p>
            </div>

            {entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center p-12 gap-3">
                    <img src={noDataIcon} alt="" className="w-40 h-auto max-h-32 object-contain opacity-50" />
                    <p className="text-base font-semibold text-stone-600">Nothing logged yet</p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {entries.map((row) => (
                        <li key={row.id} className="px-5 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                        isLogNote(row)
                                            ? 'bg-[#E0F2FE] text-[#0369A1]'
                                            : 'bg-[#E7F2EF] text-[#2A565E]'
                                    }`}>
                                    {logEntryLabel(row)}
                                </span>
                                <span className="text-xs text-gray-500">{byline(row)}</span>
                            </div>
                            <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{row.message}</p>

                            {row.comments.length > 0 && (
                                <ul className="mt-3 ml-1 space-y-3 border-l-2 border-gray-100 pl-4">
                                    {row.comments.map((comment) => (
                                        <li key={comment.id}>
                                            <p className="text-xs text-gray-500">{byline(comment)}</p>
                                            <p className="mt-0.5 text-sm text-gray-800 whitespace-pre-wrap">
                                                {comment.message}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {canWrite && (
                                <button
                                    type="button"
                                    onClick={() => openReply(row.id)}
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#346C75] hover:underline">
                                    <MessageSquare size={13} />
                                    {row.comments.length > 0 ? `Comment (${row.comments.length})` : 'Comment'}
                                </button>
                            )}

                            {replyTo === row.id && (
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const text = reply.trim();
                                        if (text) commentMutation.mutate({ text, parentId: row.id });
                                    }}
                                    className="mt-2 ml-1 space-y-2 border-l-2 border-gray-100 pl-4">
                                    <textarea
                                        value={reply}
                                        onChange={(e) => setReply(e.target.value)}
                                        maxLength={NOTE_MAX_LENGTH}
                                        rows={2}
                                        autoFocus
                                        placeholder="Write a comment"
                                        className="w-full rounded border border-stone-300 p-2 text-sm"
                                    />
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            type="button"
                                            onClick={() => setReplyTo(null)}
                                            className="bg-gray-200 text-gray-800 px-4 py-1.5 rounded hover:bg-gray-300"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={!reply.trim() || commentMutation.isPending}
                                            className="bg-[#346C75] text-white px-4 py-1.5 rounded hover:bg-[#2a565e] disabled:opacity-50"
                                        >
                                            Post
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default LegalCaseLogPanel;
